/**
 * Teleprompter Stream Fetch
 *
 * Lightweight SSE client for the teleprompter script generation endpoint.
 * Streams generated script text chunk-by-chunk so the teleprompter can
 * display a progressive "typing" effect.
 */

export interface TeleprompterFetchOptions {
  url: string;
  prompt: string;
  canvasElements?: readonly Record<string, unknown>[];
  previousScript?: string;
  onChunk?: (delta: string) => void;
  signal?: AbortSignal;
}

export interface TeleprompterFetchResult {
  script?: string;
  error?: Error;
}

export async function TeleprompterStreamFetch(
  options: TeleprompterFetchOptions,
): Promise<TeleprompterFetchResult> {
  const { url, prompt, canvasElements, previousScript, onChunk, signal } =
    options;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prompt: prompt || "",
        canvasElements: canvasElements || null,
        previousScript: previousScript || null,
      }),
      signal,
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Request failed with status ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body reader");
    }

    const decoder = new TextDecoder();
    let buffer = "";
    let fullScript = "";

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith("data: ")) {
            continue;
          }

          const data = trimmed.slice(6);
          if (data === "[DONE]") {
            break;
          }

          try {
            const chunk = JSON.parse(data);

            if (chunk.type === "script_chunk" && chunk.delta) {
              fullScript += chunk.delta;
              onChunk?.(chunk.delta);
            } else if (chunk.type === "script_done") {
              fullScript = chunk.script || fullScript;
            } else if (chunk.type === "error") {
              return { error: new Error(chunk.message || "Generation failed") };
            }
          } catch {
            // skip unparseable lines
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    return { script: fullScript };
  } catch (err: any) {
    if (err.name === "AbortError") {
      return { error: new Error("Request aborted") };
    }
    return { error: new Error(err.message || "Request failed") };
  }
}
